/**
 * KoolKonnectMagicPage — the new default KoolKonnect experience.
 *
 * Design intent (2026-07-22 directive):
 *   Match Adobe Firefly / Canva Magic / Ideogram / Krea UX:
 *   ONE upload + ONE prompt + N output tiles → parallel generation → gallery.
 *
 * Flow:
 *   1. Drop a photo (optional for pure text-to-image)
 *   2. Describe what you want in plain English
 *   3. Pick as many output tiles as you like (or "Everything" to select all)
 *   4. Click Generate → images appear immediately, videos/music stream in via
 *      polling every 4 seconds until complete.
 *   5. Download / re-run / share each output.
 *
 * Every AI call is routed through /api/koolkonnect/magic/* which itself
 * routes through the Kaharian Gateway. No direct provider calls.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import {
  Upload, Sparkles, Loader2, Download, RefreshCw, Wand2, Image as ImageIcon,
  Smartphone, Monitor, Printer, Package, Sun, User as UserIcon, Hexagon,
  Video, Music, Trash2, Check, X, ArrowRight, ChevronLeft, FileText,
  Mic, BookOpen, PenTool, Clapperboard, Zap, FolderOpen,
} from 'lucide-react';
import GenerationProgress from '../components/koolkonnect/GenerationProgress';

const API = process.env.REACT_APP_BACKEND_URL;

const ICONS = {
  instagram: ImageIcon,
  smartphone: Smartphone,
  monitor: Monitor,
  printer: Printer,
  package: Package,
  sun: Sun,
  user: UserIcon,
  hexagon: Hexagon,
  video: Video,
  'smartphone-video': Smartphone,
  music: Music,
  mic: Mic,
  book: BookOpen,
  pen: PenTool,
  clapper: Clapperboard,
};

// Brand-colored dots keep the tiles small; real logos are inline SVGs below.
const SOCIAL_LOGOS = {
  instagram: {
    color: 'from-pink-500 via-red-500 to-yellow-500',
    label: 'Instagram',
  },
  facebook: { color: 'bg-[#1877F2]', label: 'Facebook' },
  linkedin: { color: 'bg-[#0A66C2]', label: 'LinkedIn' },
  tiktok:   { color: 'bg-[#000000]', label: 'TikTok' },
  youtube:  { color: 'bg-[#FF0000]', label: 'YouTube' },
  pinterest:{ color: 'bg-[#E60023]', label: 'Pinterest' },
  spotify:  { color: 'bg-[#1DB954]', label: 'Spotify' },
};

function SocialDots({ socials }) {
  if (!socials || socials.length === 0) return null;
  return (
    <div className="flex -space-x-1.5">
      {socials.slice(0, 4).map(s => {
        const meta = SOCIAL_LOGOS[s];
        if (!meta) return null;
        const bgClass = meta.color.startsWith('from-')
          ? `bg-gradient-to-br ${meta.color}`
          : meta.color;
        return (
          <div
            key={s}
            title={meta.label}
            className={`h-5 w-5 rounded-full border-[1.5px] border-black/40 ${bgClass} shadow`}
          />
        );
      })}
    </div>
  );
}

const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

// ─── Preset tile with rotating Ken-Burns covers ───────────────────────────
function PresetTile({ preset, index, isOn, onToggle }) {
  const Icon = ICONS[preset.icon] || ImageIcon;
  const covers = preset.covers || [];
  const [coverIdx, setCoverIdx] = useState(0);

  // Rotate the 3 covers every 9 seconds — staggered per tile so they never
  // pulse together (looks alive, not synchronized like a broken slideshow).
  useEffect(() => {
    if (covers.length <= 1) return;
    const stagger = (index % 5) * 900;
    const t0 = setTimeout(() => {
      const interval = setInterval(() => {
        setCoverIdx((i) => (i + 1) % covers.length);
      }, 9000);
      window.__mgtiles = window.__mgtiles || {};
      window.__mgtiles[preset.id] = interval;
    }, stagger);
    return () => {
      clearTimeout(t0);
      if (window.__mgtiles?.[preset.id]) {
        clearInterval(window.__mgtiles[preset.id]);
        delete window.__mgtiles[preset.id];
      }
    };
  }, [covers.length, index, preset.id]);

  const activeCover = covers[coverIdx];

  return (
    <button
      onClick={onToggle}
      className={`group relative aspect-[4/3] rounded-2xl border-2 transition-all overflow-hidden text-left ${
        isOn
          ? 'border-emerald-400 shadow-lg shadow-emerald-500/30 ring-2 ring-emerald-400/20'
          : 'border-white/10 hover:border-white/30'
      }`}
      data-testid={`magic-preset-${preset.id}`}
    >
      {/* Cover carousel — Ken-Burns slow zoom, soft 2.5s cross-fade between covers */}
      <div className="absolute inset-0 bg-black">
        {covers.map((c, i) => (
          <img
            key={c}
            src={c}
            alt=""
            className={`absolute inset-0 w-full h-full object-cover ${
              i === coverIdx ? 'opacity-100 kenburns-active' : 'opacity-0'
            }`}
            style={{
              // Long ease-in-out crossfade so two covers overlap visibly at the
              // midpoint — no jarring hard switches, just a dreamy dissolve.
              transition: 'opacity 2500ms cubic-bezier(0.4, 0.0, 0.2, 1)',
            }}
            loading="lazy"
            draggable="false"
          />
        ))}
        {/* Dark scrim so text stays legible */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/10" />
      </div>

      {/* Icon + selection indicator */}
      <div className="relative h-full flex flex-col p-3 z-10">
        <div className="flex items-start justify-between">
          <div className={`h-9 w-9 rounded-xl grid place-items-center backdrop-blur-md ${isOn ? 'bg-emerald-400 text-black' : 'bg-black/60 text-white/90 border border-white/10'}`}>
            <Icon className="h-4 w-4" />
          </div>
          {isOn ? (
            <div className="h-5 w-5 rounded-full bg-emerald-400 grid place-items-center shadow">
              <Check className="h-3 w-3 text-black" />
            </div>
          ) : (
            <SocialDots socials={preset.socials} />
          )}
        </div>
        <div className="mt-auto">
          <div className="text-sm font-semibold text-white drop-shadow-lg">{preset.label}</div>
          <div className="text-[10px] text-white/85 mt-0.5 drop-shadow flex items-center gap-1">
            {preset.kind === 'image' && preset.aspect}
            {preset.kind !== 'image' && preset.kind.toUpperCase()}
            <span className="text-white/50">·</span>
            <span>{preset.credits}cr</span>
          </div>
        </div>
      </div>
    </button>
  );
}

export default function KoolKonnectMagicPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [presets, setPresets] = useState([]);
  const [selected, setSelected] = useState(new Set(['instagram_square', 'story_reel', 'facebook_ad']));
  const [prompt, setPrompt] = useState('');
  const [brandHint, setBrandHint] = useState('');
  const [refUrls, setRefUrls] = useState([]);
  const [refFiles, setRefFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [session, setSession] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [trayOpen, setTrayOpen] = useState(false);
  const pollTimerRef = useRef(null);

  // Load presets + last few sessions.
  useEffect(() => {
    (async () => {
      try {
        const [pRes, hRes] = await Promise.all([
          axios.get(`${API}/api/koolkonnect/magic/presets`, auth()),
          axios.get(`${API}/api/koolkonnect/magic/sessions?limit=6`, auth()),
        ]);
        setPresets(pRes.data.presets || []);
        setHistory(hRes.data || []);
      } catch (e) {
        // Silent — user still sees the page, just no history.
      }
    })();
  }, []);

  // Poll while any output is still processing.
  useEffect(() => {
    if (!session?.id) return;
    const anyProcessing = (session.outputs || []).some(o => o.status === 'processing');
    if (!anyProcessing) return;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/api/koolkonnect/magic/session/${session.id}`, auth());
        setSession(data);
        const stillProcessing = (data.outputs || []).some(o => o.status === 'processing');
        if (stillProcessing) pollTimerRef.current = setTimeout(tick, 4000);
      } catch (_e) {
        pollTimerRef.current = setTimeout(tick, 8000);
      }
    };
    pollTimerRef.current = setTimeout(tick, 4000);
    return () => { if (pollTimerRef.current) clearTimeout(pollTimerRef.current); };
  }, [session]);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const selectAll = () => setSelected(new Set(presets.map(p => p.id)));
  const clearAll = () => setSelected(new Set());

  const totalCredits = useMemo(() => {
    return presets.filter(p => selected.has(p.id)).reduce((sum, p) => sum + (p.credits || 0), 0);
  }, [presets, selected]);

  const onFilePick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 6 - refUrls.length;
    if (remaining <= 0) { toast.error('Maximum 6 references — remove one to add another.'); return; }
    const toUpload = files.slice(0, remaining);
    if (files.length > remaining) toast(`Only ${remaining} more allowed — extra files skipped.`, { icon: '⚠️' });
    setUploading(true);
    const fd = new FormData();
    toUpload.forEach(f => fd.append('files', f));
    try {
      const { data } = await axios.post(`${API}/api/koolkonnect/magic/upload-multi`, fd, {
        headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' },
      });
      const items = (data.references || []).map(r => ({
        url: r.url,
        publicUrl: r.public_url,
        display: r.url?.startsWith('http') ? r.url : `${API}${r.url}`,
        filename: r.filename,
      }));
      setRefFiles(prev => [...prev, ...toUpload]);
      setRefUrls(prev => [...prev, ...items]);
      toast.success(items.length > 1 ? `${items.length} references uploaded` : 'Reference uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const clearReference = () => {
    setRefFiles([]); setRefUrls([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeReference = (idx) => {
    setRefUrls(prev => prev.filter((_, i) => i !== idx));
    setRefFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const generate = async () => {
    if (!prompt.trim()) { toast.error('Describe what you want to create.'); return; }
    if (selected.size === 0) { toast.error('Pick at least one output.'); return; }
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/koolkonnect/magic/generate`, {
        prompt: prompt.trim(),
        preset_ids: Array.from(selected),
        reference_urls: refUrls.map(r => r.url),
        brand_hint: brandHint || '',
      }, auth());
      setSession(data);
      const immediate = (data.outputs || []).filter(o => o.status === 'complete').length;
      const streaming = (data.outputs || []).filter(o => o.status === 'processing').length;
      if (immediate) toast.success(`${immediate} output${immediate > 1 ? 's' : ''} ready`);
      if (streaming) toast(`${streaming} still generating — streaming in`, { icon: '🎬' });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Generation failed';
      toast.error(String(msg).slice(0, 240));
    } finally {
      setGenerating(false);
    }
  };

  const loadSession = async (sid) => {
    try {
      const { data } = await axios.get(`${API}/api/koolkonnect/magic/session/${sid}`, auth());
      setSession(data);
      if (data.prompt) setPrompt(data.prompt);
      if (data.brand_hint) setBrandHint(data.brand_hint);
      if (Array.isArray(data.reference_urls) && data.reference_urls.length) {
        setRefUrls(data.reference_urls.map(u => ({
          url: u,
          publicUrl: u.startsWith('http') ? u : u,
          display: u.startsWith('http') ? u : `${API}${u}`,
          filename: '',
        })));
      } else if (data.reference_url) {
        setRefUrls([{ url: data.reference_url, publicUrl: data.reference_url, display: data.reference_url.startsWith('http') ? data.reference_url : `${API}${data.reference_url}`, filename: '' }]);
      }
    } catch (_e) { /* ignore */ }
  };

  const regenerateOne = async (presetId) => {
    if (!session?.id) return;
    try {
      const { data } = await axios.post(`${API}/api/koolkonnect/magic/generate`, {
        prompt: session.prompt || prompt.trim(),
        preset_ids: [presetId],
        reference_urls: (session.reference_urls && session.reference_urls.length ? session.reference_urls : refUrls.map(r => r.url)),
        brand_hint: session.brand_hint || brandHint || '',
        session_id: session.id,
      }, auth());
      setSession(data);
      toast.success('Regenerating…');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Retry failed');
    }
  };

  return (
    <div className="min-h-screen bg-[#050510] text-white" data-testid="koolkonnect-magic">
      {/* Ken-Burns slow zoom for active cover — 32s cycle, no snap-back */}
      <style>{`
        @keyframes kenburns {
          0%   { transform: scale(1.04) translate(0, 0); }
          50%  { transform: scale(1.12) translate(-1%, 0.6%); }
          100% { transform: scale(1.04) translate(0, 0); }
        }
        .kenburns-active { animation: kenburns 32s ease-in-out infinite; will-change: transform; }
      `}</style>      {/* ─── Header ───────────────────────────────────────────────────── */}
      <div className="border-b border-white/5 bg-gradient-to-b from-white/[0.03] to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl bg-gradient-to-br from-emerald-400 to-cyan-500 grid place-items-center shadow-lg shadow-emerald-500/30 shrink-0">
              <Sparkles className="h-5 w-5 text-black" />
            </div>
            <div className="min-w-0">
              <div className="text-base sm:text-xl font-semibold leading-tight truncate">KoolKonnect Magic</div>
              <div className="text-[10px] sm:text-xs text-white/50 truncate">One prompt. Every format. Studio quality.</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/koolkonnect/projects')}
              className="text-white/60 hover:text-white hover:bg-white/5 px-2 sm:px-3"
              data-testid="open-advanced-projects"
            >
              <span className="hidden sm:inline">Advanced Projects </span>
              <span className="sm:hidden">Projects</span>
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-8 grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-5 sm:gap-8 pb-44 md:pb-32 lg:pb-8">
        {/* ─── Left: input panel (order-2 on mobile so tiles are visible first) ─── */}
        <div className="space-y-5 order-2 lg:order-1">
          {/* Reference photos — up to 6 for Nano Banana composition */}
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">
              Reference photos (optional · up to 6)
            </label>
            <div className="grid grid-cols-3 gap-2">
              {refUrls.map((r, idx) => (
                <div key={idx} className="relative rounded-xl overflow-hidden border border-white/10 group aspect-square" data-testid={`magic-ref-${idx}`}>
                  <img src={r.display} alt={`ref-${idx}`} className="w-full h-full object-cover" />
                  <button
                    onClick={() => removeReference(idx)}
                    className="absolute top-1 right-1 p-1 rounded-full bg-black/60 hover:bg-red-500 text-white opacity-0 group-hover:opacity-100 transition"
                    data-testid={`magic-remove-ref-${idx}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                  {idx === 0 && (
                    <div className="absolute bottom-1 left-1 text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/95 text-black font-medium">
                      Primary
                    </div>
                  )}
                </div>
              ))}
              {refUrls.length < 6 && (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-white/10 hover:border-emerald-400/50 rounded-xl aspect-square flex items-center justify-center cursor-pointer transition"
                  data-testid="magic-upload-dropzone"
                >
                  {uploading ? (
                    <Loader2 className="h-5 w-5 animate-spin text-white/60" />
                  ) : (
                    <div className="text-center px-2">
                      <Upload className="h-5 w-5 mx-auto mb-1 text-white/40" />
                      <div className="text-[10px] text-white/60 leading-tight">
                        {refUrls.length === 0 ? 'Drop products / people / logos' : 'Add another'}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="mt-2 text-[10px] text-white/40 flex items-center justify-between">
              <span>{refUrls.length}/6 · JPG, PNG, WebP up to 10MB each</span>
              {refUrls.length > 0 && (
                <button onClick={clearReference} className="text-white/60 hover:text-white underline" data-testid="magic-clear-references">
                  Clear all
                </button>
              )}
            </div>
            {refUrls.length > 0 && (
              <div className="mt-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-2 text-[10px] text-emerald-200">
                <Check className="h-3 w-3 inline mr-1" />
                <strong>Identity locked:</strong> AI will preserve exactly the faces, products and details from your references.
                {refUrls.length > 1 && ' All ' + refUrls.length + ' will be composed together.'}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={onFilePick}
              data-testid="magic-upload-input"
            />
          </div>

          {/* Prompt */}
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Describe what you want</label>
            <Textarea
              rows={5}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="A luxury smart ring on a marble surface, morning light streaming through a window, minimalist and premium."
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-emerald-400/50 rounded-xl resize-none"
              data-testid="magic-prompt-input"
            />
            <div className="mt-2 flex flex-wrap gap-1.5">
              {['Studio quality', 'Golden hour', 'Editorial magazine', 'Cinematic', 'Neon nightlife'].map(chip => (
                <button key={chip}
                        onClick={() => setPrompt(prompt ? `${prompt}, ${chip}` : chip)}
                        className="text-[10px] px-2 py-1 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/70">
                  + {chip}
                </button>
              ))}
            </div>
          </div>

          {/* Brand hint */}
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Brand style (optional)</label>
            <Input value={brandHint} onChange={(e) => setBrandHint(e.target.value)}
                   placeholder="e.g. Emerald + gold, minimalist, luxury"
                   className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl"
                   data-testid="magic-brand-hint" />
          </div>

          {/* Selection summary + Generate */}
          <div className="pt-2 border-t border-white/10">
            <div className="flex items-center justify-between text-xs text-white/50 mb-3">
              <div><span className="text-white font-semibold">{selected.size}</span> selected · ~{totalCredits} credits</div>
              <div className="flex gap-2">
                <button onClick={selectAll} className="text-white/60 hover:text-white">All</button>
                <span className="text-white/20">·</span>
                <button onClick={clearAll} className="text-white/60 hover:text-white">Clear</button>
              </div>
            </div>
            <Button
              onClick={generate}
              disabled={generating || selected.size === 0 || !prompt.trim()}
              className="w-full h-12 bg-gradient-to-r from-emerald-400 to-cyan-500 hover:from-emerald-500 hover:to-cyan-600 text-black font-medium text-base rounded-xl shadow-lg shadow-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="magic-generate-btn"
            >
              {generating ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Casting the spell…</> : <><Wand2 className="h-5 w-5 mr-2" /> Generate {selected.size} output{selected.size !== 1 ? 's' : ''}</>}
            </Button>
          </div>

          {/* Recent sessions — button opens a bottom sheet (saves vertical space) */}
          {history.length > 0 && (
            <button
              onClick={() => setHistoryOpen(true)}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition text-sm text-white/80"
              data-testid="magic-history-open"
            >
              <span className="flex items-center gap-2">
                <ChevronLeft className="h-4 w-4 text-white/50" />
                Recent sessions
              </span>
              <Badge className="text-[10px] bg-white/10 text-white/70 border-0">{history.length}</Badge>
            </button>
          )}
        </div>

        {/* ─── Right: preset tiles + results (order-1 on mobile) ─────── */}
        <div className="space-y-6 order-1 lg:order-2">
          {/* Preset grid */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-sm font-semibold">Pick your outputs</div>
                <div className="text-xs text-white/50">One prompt → every format you need.</div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {presets.map((p, idx) => (
                <PresetTile
                  key={p.id}
                  preset={p}
                  index={idx}
                  isOn={selected.has(p.id)}
                  onToggle={() => toggle(p.id)}
                />
              ))}
            </div>
          </div>

          {/* Session outputs */}
          {session && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm font-semibold">Your session</div>
                  <div className="text-xs text-white/50 truncate max-w-xl">{session.prompt}</div>
                </div>
                <div className="text-xs text-white/50">
                  {(session.outputs || []).filter(o => o.status === 'complete').length}/{(session.outputs || []).length} ready
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {(session.outputs || []).map((out) => (
                  <OutputCard key={out.id} out={out} onRegenerate={regenerateOne} />
                ))}
              </div>
            </div>
          )}

          {!session && (
            <Card className="border-dashed border-white/10 bg-transparent">
              <CardContent className="py-14 text-center text-white/40 text-sm">
                Pick your outputs, describe what you want, then hit <span className="text-emerald-400">Generate</span>.
                <br />
                Everything shows up here.
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Mobile sticky Generate bar — sits ABOVE the app BottomNav (h-16) so it
          never gets covered. Tap the "N selected" chip to expand a peek tray
          showing selected outputs with per-item remove chips. */}
      {selected.size > 0 && (
        <div
          className="fixed left-0 right-0 z-40 lg:hidden bg-[#050510]/95 backdrop-blur-xl border-t border-white/10 shadow-2xl"
          style={{ bottom: 'calc(4rem + env(safe-area-inset-bottom, 0px))' }}
          data-testid="magic-mobile-cta"
        >
          {/* Peek tray — chips of selected outputs, opens on tap of the summary. */}
          {trayOpen && (
            <div className="px-4 pt-3 pb-1 border-b border-white/10 max-h-[40vh] overflow-y-auto" data-testid="magic-mobile-tray">
              <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Your selection · tap × to remove</div>
              <div className="flex flex-wrap gap-1.5">
                {Array.from(selected).map(pid => {
                  const p = presets.find(x => x.id === pid);
                  if (!p) return null;
                  return (
                    <button
                      key={pid}
                      onClick={(e) => { e.stopPropagation(); toggle(pid); }}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-100 text-[11px]"
                      data-testid={`magic-tray-chip-${pid}`}
                    >
                      <span>{p.label}</span>
                      <X className="h-3 w-3" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="px-4 py-3 flex items-center gap-3">
            <button
              onClick={() => setTrayOpen(v => !v)}
              className="text-[11px] text-white/60 leading-tight text-left flex items-center gap-1"
              data-testid="magic-mobile-summary"
            >
              <span className={`inline-block transition-transform ${trayOpen ? 'rotate-180' : ''}`}>▲</span>
              <span>
                <span className="text-white font-medium text-xs block">{selected.size} selected</span>
                <span>~{totalCredits} credits</span>
              </span>
            </button>
            <Button
              onClick={generate}
              disabled={generating || !prompt.trim()}
              className="flex-1 h-11 bg-gradient-to-r from-emerald-400 to-cyan-500 hover:from-emerald-500 hover:to-cyan-600 text-black font-medium rounded-xl disabled:opacity-40"
              data-testid="magic-generate-btn-mobile"
            >
              {generating ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Casting…</> : <><Wand2 className="h-4 w-4 mr-1.5" /> Generate</>}
            </Button>
          </div>
          {!prompt.trim() && (
            <div className="text-[10px] text-amber-300/90 pb-2 text-center">Add a prompt above ↑ to enable Generate</div>
          )}
        </div>
      )}

      {/* Recent Sessions — bottom sheet on mobile, side sheet on desktop */}
      <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
        <SheetContent side="bottom" className="bg-[#0a0a1a] border-white/10 text-white max-h-[75vh]" data-testid="magic-history-sheet">
          <SheetHeader>
            <SheetTitle className="text-white">Recent sessions</SheetTitle>
            <SheetDescription className="text-white/50">Reopen any session to see its outputs or regenerate.</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-1.5 overflow-y-auto max-h-[55vh]">
            {history.map(h => (
              <button
                key={h.id}
                onClick={() => { loadSession(h.id); setHistoryOpen(false); }}
                className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-white/5 transition text-sm text-white/85 flex items-center gap-2"
                data-testid={`magic-history-${h.id}`}
              >
                <ChevronLeft className="h-3 w-3 text-white/40 shrink-0" />
                <span className="truncate flex-1">{h.prompt || 'Untitled'}</span>
                <Badge className="text-[10px] bg-white/10 text-white/60 border-0 shrink-0">
                  {(h.outputs || []).length}
                </Badge>
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ─── Output card ─────────────────────────────────────────────────────────────
function OutputCard({ out, onRegenerate }) {
  const navigate = useNavigate();
  const isImg = out.kind === 'image';
  const isVid = out.kind === 'video';
  const isMus = out.kind === 'music';
  const isTxt = out.kind === 'text';
  const done = out.status === 'complete';
  const failed = out.status === 'failed';
  const processing = out.status === 'processing';
  // Kaharian assets stored locally return relative /api/uploads URLs — prepend API host.
  const displayUrl = out.url && !out.url.startsWith('http') ? `${API}${out.url}` : out.url;

  const download = async () => {
    if (!displayUrl) return;
    try {
      const res = await fetch(displayUrl, { mode: 'cors' });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${out.label.replace(/\s+/g, '-').toLowerCase()}.${isImg ? 'png' : isVid ? 'mp4' : isMus ? 'mp3' : 'md'}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_e) {
      window.open(displayUrl, '_blank');
    }
  };

  return (
    <div className="rounded-2xl overflow-hidden border border-white/10 bg-white/[0.02] group" data-testid={`magic-output-${out.preset_id}`}>
      <div className="relative aspect-[4/3] bg-black/40 grid place-items-center overflow-hidden">
        {processing && (
          <GenerationProgress
            kind={out.kind}
            startedAt={out.started_at || out.created_at}
            complete={false}
            failed={false}
            variant="full"
          />
        )}
        {failed && (
          <div className="text-center px-4">
            <X className="h-6 w-6 text-red-400 mx-auto mb-2" />
            <div className="text-xs text-white/80">Couldn&apos;t generate</div>
            <div className="text-[10px] text-white/40 mt-1 line-clamp-2">{out.error}</div>
            {onRegenerate && (
              <button
                onClick={() => onRegenerate(out.preset_id)}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] bg-emerald-400 hover:bg-emerald-300 text-black font-medium"
                data-testid={`magic-retry-${out.preset_id}`}
              >
                <RefreshCw className="h-3 w-3" /> Regenerate
              </button>
            )}
          </div>
        )}
        {done && isImg && <img src={displayUrl} alt={out.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
        {done && isVid && (
          <video src={displayUrl} controls className="w-full h-full object-cover" preload="metadata" />
        )}
        {done && isTxt && (
          <div className="w-full h-full p-4 overflow-y-auto text-white/85 text-[11px] leading-relaxed bg-gradient-to-br from-slate-900 to-slate-950">
            <div className="max-w-none whitespace-pre-wrap font-serif">
              {(out.text || '').split('\n').slice(0, 20).join('\n')}
              {out.text && out.text.split('\n').length > 20 && <div className="text-white/40 text-[10px] mt-2">…({out.text.length.toLocaleString()} characters — download to read all)</div>}
            </div>
          </div>
        )}
        {done && isMus && (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-500/20 to-cyan-500/20">
            <div className="text-center w-full px-4">
              <Music className="h-8 w-8 mx-auto text-emerald-300 mb-3" />
              <audio src={displayUrl} controls className="w-full" />
            </div>
          </div>
        )}
      </div>
      <div className="p-3 flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{out.label}</div>
          <div className="text-[10px] text-white/40">
            {done ? (isMus || isVid || isImg ? 'Saved to Library ✓' : 'Ready') : failed ? 'Failed' : 'Processing'}
          </div>
        </div>
        {done && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => {
                navigate(`/koolkonnect/projects?tab=library&kind=${out.kind}`);
                toast.success('Opened in Library');
              }}
              className="p-1.5 rounded-md text-white/70 hover:text-emerald-300 hover:bg-white/10 transition"
              title="Open in Library"
              data-testid={`magic-open-library-${out.preset_id}`}
            >
              <FolderOpen className="h-4 w-4" />
            </button>
            <Button size="sm" variant="ghost" onClick={download}
                    className="text-white/70 hover:text-white hover:bg-white/10"
                    data-testid={`magic-download-${out.preset_id}`}>
              <Download className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
